import api from './api';

/**
 * Service pour les fonctionnalités financières d'administration
 * Interface admin pour voir toutes les données financières des propriétaires
 */
class AdminFinancialService {

  /**
   * Récupère les réservations groupées par propriétaire
   * @param {Object} options - Options de filtrage et pagination
   * @returns {Promise<Object>} Données groupées par propriétaire
   */
  async getReservationsByOwner(options = {}) {
    try {
      const { 
        page = 1, 
        limit = 10, 
        dateStart = null, 
        dateEnd = null, 
        ownerId = null,
        reservationStatus = null,
        paymentMethod = null,
        ownerEmail = null,
        currency = null // ✅ Ajout du filtre par devise
      } = options;

      console.log('🔄 [AdminFinancialService] Fetching reservations by owner...', {
        page,
        limit,
        dateStart,
        dateEnd,
        ownerId,
        reservationStatus,
        paymentMethod,
        ownerEmail,
        currency // ✅ Ajout du currency dans les logs
      });

      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      
      if (dateStart) {
        params.append('dateStart', dateStart);
      }
      if (dateEnd) {
        params.append('dateEnd', dateEnd);
      }
      if (ownerId) {
        params.append('ownerId', ownerId);
      }
      if (reservationStatus) {
        params.append('reservationStatus', reservationStatus);
      }
      if (paymentMethod) {
        params.append('paymentMethod', paymentMethod);
      }
      if (ownerEmail) {
        params.append('ownerEmail', ownerEmail);
      }
      if (currency) { // ✅ Ajout du paramètre currency
        params.append('currency', currency);
      }

      const endpoint = `/admin/finances/reservations-by-owner?${params.toString()}`;
      const response = await api.get(endpoint);

      if (response.data && response.data.success) {
        const result = response.data.data;
        
        console.log('✅ [AdminFinancialService] Reservations by owner loaded:', {
          ownersCount: result.owners?.length || 0,
          totalItems: result.pagination?.totalItems || 0,
          currentPage: result.pagination?.currentPage || 1
        });

        return {
          success: true,
          data: result
        };
      } else {
        console.error('❌ [AdminFinancialService] API response indicates failure:', response.data);
        return {
          success: false,
          message: response.data?.message || 'Erreur inconnue',
          data: null
        };
      }

    } catch (error) {
      console.error('❌ [AdminFinancialService] Error in getReservationsByOwner:', error);
      
      return {
        success: false,
        message: this.getErrorMessage(error),
        data: null
      };
    }
  }

  /**
   * Récupère les statistiques financières globales
   * @param {Object} options - Options de filtrage par date
   * @returns {Promise<Object>} Statistiques globales
   */
  async getGlobalFinancialStats(options = {}) {
    try {
      const { dateStart = null, dateEnd = null } = options;

      console.log('🔄 [AdminFinancialService] Fetching global financial stats...', {
        dateStart,
        dateEnd
      });

      const params = new URLSearchParams();
      
      if (dateStart) {
        params.append('dateStart', dateStart);
      }
      if (dateEnd) {
        params.append('dateEnd', dateEnd);
      }

      const endpoint = `/admin/finances/stats?${params.toString()}`;
      const response = await api.get(endpoint);

      if (response.data && response.data.success) {
        const result = response.data.data;
        
        console.log('✅ [AdminFinancialService] Global financial stats loaded:', {
          paymentsCount: result.payments?.length || 0,
          depositsCount: result.deposits?.length || 0
        });

        return {
          success: true,
          data: result
        };
      } else {
        console.error('❌ [AdminFinancialService] API response indicates failure:', response.data);
        return {
          success: false,
          message: response.data?.message || 'Erreur inconnue',
          data: null
        };
      }

    } catch (error) {
      console.error('❌ [AdminFinancialService] Error in getGlobalFinancialStats:', error);
      
      return {
        success: false,
        message: this.getErrorMessage(error),
        data: null
      };
    }
  }

  /**
   * Récupère le dashboard financier admin complet
   * @param {Object} filters - Filtres de période et autres
   * @returns {Promise<Object>} Dashboard complet
   */
  async getFinancialDashboard(filters = {}) {
    try {
      console.log('🔄 [AdminFinancialService] Fetching financial dashboard...', filters);

      const params = new URLSearchParams();
      
      if (filters.startDate) {
        params.append('start_date', filters.startDate);
      }
      if (filters.endDate) {
        params.append('end_date', filters.endDate);
      }
      if (filters.currency) {
        params.append('currency', filters.currency);
      }

      const endpoint = `/admin/finances/dashboard?${params.toString()}`;
      const response = await api.get(endpoint);

      if (response.data && response.data.success) {
        const result = response.data.data;
        
        console.log('✅ [AdminFinancialService] Financial dashboard loaded:', {
          hasRevenueMetrics: !!result.revenueMetrics,
          hasPaymentMetrics: !!result.paymentMetrics
        });

        return {
          success: true,
          data: result
        };
      } else {
        console.error('❌ [AdminFinancialService] API response indicates failure:', response.data);
        return {
          success: false,
          message: response.data?.message || 'Erreur inconnue',
          data: null
        };
      }

    } catch (error) {
      console.error('❌ [AdminFinancialService] Error in getFinancialDashboard:', error);
      
      return {
        success: false,
        message: this.getErrorMessage(error),
        data: null
      };
    }
  }

  /**
   * Utilitaire pour extraire un message d'erreur lisible
   * @param {Error} error - Erreur à traiter
   * @returns {string} Message d'erreur
   */
  getErrorMessage(error) {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.response?.status === 403) {
      return 'Accès non autorisé - Droits administrateur requis';
    }
    if (error.response?.status === 401) {
      return 'Non authentifié - Veuillez vous reconnecter';
    }
    if (error.response?.status >= 500) {
      return 'Erreur serveur - Veuillez réessayer plus tard';
    }
    if (error.message) {
      return error.message;
    }
    return 'Erreur inconnue';
  }

  /**
   * Calcule les totaux pour un propriétaire à partir de ses réservations
   * @param {Array} reservations - Liste des réservations
   * @returns {Object} Totaux calculés
   */
  calculateOwnerTotals(reservations) {
    const totals = {
      reservationsCount: reservations.length,
      totalAmount: 0,
      totalRental: 0,
      totalCommission: 0,
      totalTax: 0,
      totalPenalty: 0,
      totalDeposits: 0
    };

    reservations.forEach(reservation => {
      totals.totalAmount += parseFloat(reservation.total_amount || 0);
      
      if (reservation.payment_details) {
        totals.totalRental += parseFloat(reservation.payment_details.rental?.total || 0);
        totals.totalCommission += parseFloat(reservation.payment_details.commission?.total || 0);
        totals.totalTax += parseFloat(reservation.payment_details.tax?.total || 0);
        totals.totalPenalty += parseFloat(reservation.payment_details.penalty?.total || 0);
        totals.totalDeposits += parseFloat(reservation.payment_details.deposits?.total || 0);
      }
    });

    return totals;
  }

  /**
   * Formate les données de paiement pour l'affichage
   * @param {Object} paymentDetails - Détails des paiements
   * @returns {Object} Données formatées
   */
  formatPaymentDetails(paymentDetails) {
    if (!paymentDetails) return {};

    return {
      rental: {
        ...paymentDetails.rental,
        formatted_total: this.formatCurrency(paymentDetails.rental?.total || 0)
      },
      commission: {
        ...paymentDetails.commission,
        formatted_total: this.formatCurrency(paymentDetails.commission?.total || 0)
      },
      tax: {
        ...paymentDetails.tax,
        formatted_total: this.formatCurrency(paymentDetails.tax?.total || 0)
      },
      penalty: {
        ...paymentDetails.penalty,
        formatted_total: this.formatCurrency(paymentDetails.penalty?.total || 0)
      },
      deposits: {
        ...paymentDetails.deposits,
        formatted_total: this.formatCurrency(paymentDetails.deposits?.total || 0)
      },
      summary: {
        ...paymentDetails.summary,
        formatted_total_payments: this.formatCurrency(paymentDetails.summary?.totalPayments || 0),
        formatted_total_deposits: this.formatCurrency(paymentDetails.summary?.totalDeposits || 0),
        formatted_grand_total: this.formatCurrency(paymentDetails.summary?.grandTotal || 0)
      }
    };
  }

  /**
   * Formate une valeur monétaire
   * @param {number} amount - Montant à formater
   * @param {string} currency - Devise (défaut EUR)
   * @returns {string} Montant formaté
   */
  formatCurrency(amount, currency = 'EUR') {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency
    }).format(amount);
  }

  /**
   * Récupère les devises disponibles dans les réservations
   * @returns {Promise<Array>} Liste des devises disponibles
   */
  async getAvailableCurrencies() {
    try {
      console.log('🔄 [AdminFinancialService] Fetching available currencies...');
      
      const response = await api.get('/admin/finances/available-currencies');

      if (response.data && response.data.success) {
        const currencies = response.data.data.currencies;
        
        console.log('✅ [AdminFinancialService] Available currencies loaded:', currencies);
        
        return {
          success: true,
          data: currencies
        };
      } else {
        console.error('❌ [AdminFinancialService] Invalid response for available currencies');
        return { success: false, data: [] };
      }

    } catch (error) {
      console.error('❌ [AdminFinancialService] Error fetching available currencies:', error);
      return { success: false, data: [] };
    }
  }

  /**
   * Récupère la liste des dépôts de garantie avec options de filtrage
   * @param {Object} options - Options de filtrage et pagination
   * @returns {Promise<Object>} Liste paginée des dépôts
   */
  async getDeposits(options = {}) {
    try {
      const {
        page = 1,
        limit = 20,
        status = null,
        overdue = false,
        userId = null,
        listingId = null,
        startDate = null,
        endDate = null,
        sortBy = 'created_at',
        sortOrder = 'DESC',
        paymentMethod = null // Ajout du paramètre de méthode de paiement
      } = options;

      console.log('🔄 [AdminFinancialService] Fetching deposits with options:', {
        page, limit, status, overdue, userId, listingId, startDate, endDate, sortBy, sortOrder, paymentMethod
      });

      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      params.append('sortBy', sortBy);
      params.append('sortOrder', sortOrder);
      
      if (status) {
        params.append('status', status);
      }
      if (overdue) {
        params.append('overdue', 'true');
      }
      if (userId) {
        params.append('userId', userId);
      }
      if (listingId) {
        params.append('listingId', listingId);
      }
      if (startDate) {
        params.append('startDate', startDate);
      }
      if (endDate) {
        params.append('endDate', endDate);
      }
      if (options.currency) {
        params.append('currency', options.currency);
      }
      if (paymentMethod) {
        params.append('paymentMethod', paymentMethod);
      }

      const response = await api.get(`/admin/finances/deposits?${params.toString()}`);
      
      console.log('✅ [AdminFinancialService] Deposits fetched successfully', {
        depositsCount: response.data.data?.deposits?.length || 0,
        pagination: response.data.data?.pagination
      });
     

      return {
        success: true,
        data: {
          deposits: response.data.data?.deposits || [],
          pagination: response.data.data?.pagination || {
            total_items: 0,
            total_pages: 0,
            current_page: page,
            page_size: limit
          }
        }
      };

    } catch (error) {
      console.error('❌ [AdminFinancialService] Error fetching deposits:', error);
      
      // Extraire page et limit de nouveau pour les utiliser dans le catch
      const { page = 1, limit = 20 } = options;
      
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        data: {
          deposits: [],
          pagination: {
            total_items: 0,
            total_pages: 0,
            current_page: page,
            page_size: limit
          }
        }
      };
    }
  }

  /**
   * Effectue une action administrative sur un dépôt
   * @param {string} depositId - ID du dépôt
   * @param {string} action - Type d'action (refund, release, hold, cancel)
   * @param {Object} data - Données supplémentaires pour l'action
   * @returns {Promise<Object>} Résultat de l'action
   */
  async performDepositAction(depositId, action, data = {}) {
    try {
      

      const response = await api.post(`/admin/finances/deposits/${depositId}/actions`, {
        action,
        ...data
      });

     

      return {
        success: true,
        data: response.data
      };

    } catch (error) {
      console.error('❌ [AdminFinancialService] Error performing deposit action:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  }

  /**
   * Récupère les statistiques générales des dépôts
   * @param {Object} options - Options de filtrage
   * @returns {Promise<Object>} Statistiques des dépôts
   */
  async getDepositsOverview(options = {}) {
    try {
      const {
        startDate = null,
        endDate = null,
        paymentMethod = null // Ajout du paramètre de méthode de paiement
      } = options;

      console.log('🔄 [AdminFinancialService] Fetching deposits overview with options:', {
        startDate, endDate, paymentMethod, currency: options.currency
      });

      const params = new URLSearchParams();
      if (startDate) {
        params.append('startDate', startDate);
      }
      if (endDate) {
        params.append('endDate', endDate);
      }
      if (options.currency) {
        params.append('currency', options.currency);
      }
      if (paymentMethod) {
        params.append('paymentMethod', paymentMethod);
      }

      const response = await api.get(`/admin/finances/deposits/overview?${params.toString()}`);
   

      return {
        success: true,
        data: response.data.data // ← PROBLÈME ICI ! response.data.data au lieu de response.data
      };

    } catch (error) {
      console.error('❌ [AdminFinancialService] Error fetching deposits overview:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        data: {}
      };
    }
  }
}

export default new AdminFinancialService();